// src/pages/BanquetInfoPlaceholder.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { io as socketIO } from 'socket.io-client';
axios.defaults.withCredentials = true;

/** 今天（本地）YYYY-MM-DD（僅備用） */
function todayYmd() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

/** 業務日切點：每天 01:00 本機時間 → YYYY-MM-DD */
function getDayKeyBy1AM(d = new Date()) {
  const year = d.getFullYear();
  const mon  = d.getMonth();
  const day  = d.getDate();
  const hour = d.getHours();
  const base = new Date(year, mon, day, 0, 0, 0, 0);
  if (hour < 1) base.setDate(base.getDate() - 1);
  const y = base.getFullYear();
  const m = String(base.getMonth() + 1).padStart(2, '0');
  const dd = String(base.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}
const bizDay = () => getDayKeyBy1AM();

/** 從任意時間字串擷取 HH:MM（不做時區運算） */
function pickHM(raw) {
  if (!raw) return '';
  const s = String(raw);
  const m = s.match(/(?:\b|T|\s)(\d{1,2}):(\d{2})/);
  return m ? `${m[1].padStart(2, '0')}:${m[2]}` : '';
}
/** 從牆上時間字串擷取 YYYY-MM-DD（不依賴 slice；避免 Date.toString()/ISO 混用造成誤判） */
const datePart = (raw) => {
  const s = String(raw || '').trim();
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : '';
};

function fmtEventTime(ev) {
  if (ev?.time_range) return ev.time_range;
  // 週活動：公開顯示活動開始 ~ 結束
  if (ev?.activity_time || ev?.ACTIVITY_TIME) {
    const a = pickHM(ev?.activity_time || ev?.ACTIVITY_TIME);
    const b = pickHM(ev?.end_time || ev?.END_TIME);
    if (a && b) return `${a} ~ ${b}`;
    return a || '';
  }
  const a = pickHM(ev?.start_time);
  const b = pickHM(ev?.end_time);
  if (a && b) return `${a} ~ ${b}`;
  return a || b || '';
}

function fmtVenueTime(v) {
  // 優先 *_STR（牆上時間）；若 STR 被截斷或格式異常，再 fallback START_TIME / END_TIME（HH:mm）
  const sRaw = v?.START_TIME_STR || v?.start_time_str;
  const eRaw = v?.END_TIME_STR || v?.end_time_str;
  let a = pickHM(sRaw);
  let b = pickHM(eRaw);
  if (!a) a = pickHM(v?.START_TIME);
  if (!b) b = pickHM(v?.END_TIME);
  const sd = datePart(sRaw);
  const ed = datePart(eRaw);
  const isNextDay = !!(sd && ed && sd !== ed);
  if (a && b) return `${a} ~ ${isNextDay ? '隔日 ' : ''}${b}`;
  return a || b || '';
}

/** 排序鍵（以 HH:MM 字串） */
const sortKeyEvent = (ev) =>
  pickHM(ev?.start_time) ||
  (String(ev?.time_range || '').match(/(\d{1,2}):(\d{2})/)
    ? `${RegExp.$1.padStart(2, '0')}:${RegExp.$2}`
    : '99:99');
const sortKeyVenue = (v) => pickHM(v?.START_TIME) || '99:99';

// 場地是否標記為「卡位 / 場勘」（舊表 VENUE 字串 + VB ORDER_KIND / IS_SITE_VISIT）
const isFlaggedVenue = (v) => {
  if (!v) return false;
  if (v.IS_SITE_VISIT === 1 || v.IS_SITE_VISIT === true) return true;
  if (String(v.ORDER_KIND || '').trim() === 'site_visit') return true;
  if (String(v.STATUS || '').trim().toUpperCase() === 'SITE_VISIT') return true;
  return /卡位|場勘/.test(String(v.VENUE || ''));
};

/** 輕量哈希（用於比對資料是否有變化） */
function fastHash(obj) {
  const stable = JSON.stringify(obj, Object.keys(obj || {}).sort());
  let h = 0;
  for (let i = 0; i < stable.length; i++) h = (h ^ stable.charCodeAt(i)) >>> 0;
  return h;
}
function listHash(list) {
  if (!Array.isArray(list)) return 0;
  const proj = list.map((x) => ({
    id: x.id ?? x.ID ?? x.ORDER_ID ?? '',
    t1: x.title ?? x.VENUE ?? '',
    t2: x.owner ?? x.CUSTNAME ?? '',
    t3: x.PHONE ?? x.phone ?? '',
    t4: x.handover_notes ?? x.COUNTER_NOTE ?? '',
    t5: x.beverage_need ?? '',
    t6: x.internal_brief ?? x.activityInternalBrief ?? '',
    t7: x.activityName ?? x.ACTIVITY_NAME ?? '',
    a: x.start_time ?? x.START_TIME ?? '',
    b: x.end_time ?? x.END_TIME ?? '',
    r: x.time_range ?? '',
    s: x.STATUS ?? '',
    ci: !!(x.checked_in || x.CHECKED_IN),
  }));
  return fastHash({ L: proj.length, proj });
}

// 日期工具函數（移到組件外）
function parseDate(s) {
  const m = String(s || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return new Date();
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}
function yyyyMMdd(d) {
  const y = d.getFullYear();
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const dd = `${d.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

export default function BanquetInfoPlaceholder({ todayOnly = false } = {}) {
  // 如果 todayOnly 為 true，強制使用今天，不允許切換日期
  const [ymd, setYmd] = useState(bizDay());
  const [loading, setLoading] = useState(true);
  const initialLoadRef = useRef(true); // 追蹤是否為初次載入
  const [events, setEvents] = useState([]);
  const [venues, setVenues] = useState([]);
  const [paneTab, setPaneTab] = useState('events');
  const [bnNarrow, setBnNarrow] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  useEffect(() => {
    const onR = () => setBnNarrow(window.innerWidth < 768);
    window.addEventListener('resize', onR);
    return () => window.removeEventListener('resize', onR);
  }, []);
  const manualDateRef = useRef(false); // 追蹤是否手動切換過日期
  const socketRef = useRef(null); // Socket.IO 連接
  const mountedRef = useRef(true); // 追蹤組件是否已掛載

  // 如果 todayOnly，確保 ymd 始終是今天
  useEffect(() => {
    if (todayOnly) {
      const today = bizDay();
      if (ymd !== today) {
        setYmd(today);
        manualDateRef.current = false;
      }
    }
  }, [todayOnly, ymd]);

  const changeDate = (days) => {
    if (todayOnly) return; // 如果 todayOnly，不允許切換日期
    const d = parseDate(ymd);
    d.setDate(d.getDate() + days);
    const newYmd = yyyyMMdd(d);
    manualDateRef.current = true; // 標記為手動切換
    
    // 日期改變時，立即清空資料和 ETag，避免顯示舊資料
    setEvents([]);
    setVenues([]);
    evETagRef.current = '';
    vnETagRef.current = '';
    evHashRef.current = 0;
    vnHashRef.current = 0;
    
    setYmd(newYmd);
  };

  const dateLabel = useMemo(() => {
    const d = parseDate(ymd);
    const today = new Date();
    const isToday = yyyyMMdd(d) === yyyyMMdd(today);
    const dayNames = ['日', '一', '二', '三', '四', '五', '六'];
    return isToday
      ? `今天 (${d.getMonth() + 1}/${d.getDate()})`
      : `${d.getMonth() + 1}/${d.getDate()}（${dayNames[d.getDay()]}）`;
  }, [ymd]);

  const evHashRef = useRef(0);
  const vnHashRef = useRef(0);
  const evETagRef = useRef('');
  const vnETagRef = useRef('');
  const fetchLockRef = useRef(false);
  const abortRef = useRef(null);

  // 使用 useCallback（箭頭函式版，避免某些 Babel 邊緣 case）
  const fetchData = useCallback(
    async (dateStr = ymd, force = false) => {
      // 如果正在請求中，且不是強制更新，則跳過
      if (fetchLockRef.current && !force) return;
      fetchLockRef.current = true;

      if (abortRef.current) {
        try {
          abortRef.current.abort();
        } catch (e) {
          // ignore
        }
      }
      const controller = new AbortController();
      abortRef.current = controller;

      // 強制更新時不使用 ETag
      const commonCfg = (et) => ({
        params: { date: dateStr },
        signal: controller.signal,
        headers: et && !force ? { 'If-None-Match': et } : undefined,
        validateStatus: (s) => (s >= 200 && s < 300) || s === 304,
      });

      try {
        // 只在初次載入時顯示 loading，日期切換或輪詢時不顯示
        if (initialLoadRef.current) {
          setLoading(true);
        }

        // 直接使用合併 API（效能較好，減少請求次數）
        let gotEvents = null;
        let gotVenues = null;
        let etEv = '';
        let etVn = '';

        try {
          // 強制更新時不使用 ETag，確保每次都重新查詢資料庫
          const cfg = force 
            ? { params: { date: dateStr }, signal: controller.signal }
            : commonCfg(evETagRef.current || vnETagRef.current);
          
          const { data, headers, status } = await axios.get('/api/banquet/today', cfg);
          
          if (status !== 304 || force) {
            // 強制更新時，即使 304 也要重新解析資料
            gotEvents = Array.isArray(data?.events) ? data.events : [];
            gotVenues = Array.isArray(data?.venues) ? data.venues : [];
            etEv = headers?.etag || '';
            etVn = headers?.etag || '';
          } else {
            // 非強制更新且 304 時，若活動與包場皆為空可能是狀態遺失，強制重新載入
            if (events.length === 0 && venues.length === 0) {
              fetchLockRef.current = false;
              fetchData(dateStr, true);
              return;
            }
            gotEvents = events;
            gotVenues = venues;
          }
        } catch (e) {
          // 如果請求失敗，且是強制更新（日期切換），清空資料
          if (force) {
            gotEvents = [];
            gotVenues = [];
          } else {
            // 非強制更新時，保留舊資料
            if (!e?.response || e.response.status !== 304) {
              console.warn('banquet/today fetch error:', e?.message);
            }
            gotEvents = events;
            gotVenues = venues;
          }
        }

        // 若是強制更新（通常代表日期切換），確保沒有資料時會顯示「沒有活動 / 沒有包場」
        if (force) {
          if (gotEvents == null) gotEvents = [];
          if (gotVenues == null) gotVenues = [];
        }

        // 新資料是什麼就貼什麼：API 回傳就直接覆寫，不比較 hash
        if (gotEvents !== null) {
          evHashRef.current = listHash(gotEvents);
          setEvents(gotEvents);
        }
        if (gotVenues !== null) {
          vnHashRef.current = listHash(gotVenues);
          setVenues(gotVenues);
        }

        if (etEv) evETagRef.current = etEv;
        if (etVn) vnETagRef.current = etVn;
      } catch (e) {
        // 忽略中止錯誤；保留舊資料避免閃爍
        if (e?.name !== 'AbortError' && e?.name !== 'CanceledError') {
          console.warn('fetchData error:', e?.message);
        }

        // 若是強制更新（通常是切到其他日期），但發生錯誤，為避免誤解日期，清空顯示
        if (force) {
          setEvents([]);
          setVenues([]);
          evHashRef.current = listHash([]);
          vnHashRef.current = listHash([]);
        }
      } finally {
        setLoading(false);
        // 初次載入完成後標記為已完成
        if (initialLoadRef.current) {
          initialLoadRef.current = false;
        }
        // 確保 fetchLock 一定會被釋放
        setTimeout(() => {
          fetchLockRef.current = false;
        }, 100);
      }
    },
    [ymd, events.length, venues.length]
  ); // 只依賴 ymd（以及目前是否有資料）

  // 初次載入 + 日期改變時立即更新
  useEffect(() => {
    // 日期改變時，立即清空 ETag 和 hash，強制重新載入
    evETagRef.current = '';
    vnETagRef.current = '';
    
    // 日期改變時：如果已有資料，不顯示 loading（避免跳動）
    // 如果沒有資料，視為初次載入，顯示 loading
    const hasData = events.length > 0 || venues.length > 0;
    if (!hasData) {
      initialLoadRef.current = true; // 沒有資料，視為初次載入
      setLoading(true);
    } else {
      initialLoadRef.current = false; // 已有資料，切換日期時不顯示 loading
    }
    
    let alive = true;
    const controller = new AbortController();
    abortRef.current = controller;
    
    (async () => {
      if (!alive) return;
      // 日期改變時立即強制更新資料，使用新的日期參數，不使用 ETag
      try {
        const { data, headers } = await axios.get(
          '/api/banquet/today',
          {
            params: { date: ymd },
            signal: controller.signal,
          }
        );
        
        if (!alive) return;
        
        const gotEvents = Array.isArray(data?.events) ? data.events : [];
        const gotVenues = Array.isArray(data?.venues) ? data.venues : [];
        
        // 立即更新資料
        const nextEvHash = listHash(gotEvents);
        const nextVnHash = listHash(gotVenues);
        evHashRef.current = nextEvHash;
        vnHashRef.current = nextVnHash;
        setEvents(gotEvents);
        setVenues(gotVenues);
        
        // 更新 ETag
        if (headers?.etag) {
          evETagRef.current = headers.etag;
          vnETagRef.current = headers.etag;
        }
      } catch (e) {
        if (!alive) return;
        if (e?.name !== 'AbortError' && e?.name !== 'CanceledError') {
          console.warn('banquet/today fetch error:', e?.message);
          // 日期改變時發生錯誤，清空資料
          setEvents([]);
          setVenues([]);
          evHashRef.current = 0;
          vnHashRef.current = 0;
        }
      } finally {
        if (alive) {
          setLoading(false);
          if (initialLoadRef.current) {
            initialLoadRef.current = false;
          }
        }
      }
    })();
    
    return () => {
      alive = false;
      try {
        controller.abort();
      } catch {}
    };
  }, [ymd]); // 只依賴 ymd，確保日期改變時一定會重新載入

  // === 推送：Socket.IO 監聽預約更新，有變動就重載宴會/包場 ===
  const SOCKET_DEBUG = true;
  useEffect(() => {
    mountedRef.current = true;
    const s = socketIO('/', { path: '/socket.io' });
    socketRef.current = s;

    const onBookingsUpdate = () => {
      if (!mountedRef.current) return;
      if (SOCKET_DEBUG) console.log('[櫃台-宴會] 收到推送 bookings:update，重新載入');
      fetchData(ymd, true);
    };

    const onBanquetCheckin = () => {
      if (!mountedRef.current) return;
      if (SOCKET_DEBUG) console.log('[櫃台-宴會] 收到推送 banquet:checkin，重新載入');
      fetchData(ymd, true);
    };

    const onConnect = () => {
      if (SOCKET_DEBUG) console.log('[櫃台-宴會] 推送 Socket 已連接，加入 room:', ymd);
      s.emit('bookings:join', { day: ymd, section: '__ALL__' });
    };

    s.on('connect', onConnect);
    s.on('bookings:update', onBookingsUpdate);
    s.on('bookings:update-sections', onBookingsUpdate);
    s.on('banquet:checkin', onBanquetCheckin);

    // 如果已經連接，立即加入 room
    if (s.connected) {
      onConnect();
    }

    return () => {
      mountedRef.current = false;
      try { 
        s.emit('bookings:leave', { day: ymd, section: '__ALL__' }); 
      } catch {}
      s.off('connect', onConnect);
      s.off('bookings:update', onBookingsUpdate);
      s.off('bookings:update-sections', onBookingsUpdate);
      s.off('banquet:checkin', onBanquetCheckin);
      s.close();
    };
  }, [ymd, fetchData]);

  // 當日期改變時，重新加入 Socket room
  useEffect(() => {
    const s = socketRef.current;
    if (!s || !s.connected) return;
    console.log('[BanquetInfoPlaceholder] 日期改變，重新加入 room:', ymd);
    s.emit('bookings:join', { day: ymd, section: '__ALL__' });
  }, [ymd]);

  // === 輪詢 + 推送：每分鐘重新讀取完整資料，API 回什麼就顯示什麼 ===
  const POLL_MS = 60 * 1000;
  const DEBUG = true;
  useEffect(() => {
    if (!mountedRef.current) return;
    let timer = null;
    let stopped = false;
    if (DEBUG) console.log('[櫃台-宴會] 輪詢 effect 啟動', { visibility: document.visibilityState, ymd });

    const tick = () => {
      if (stopped || !mountedRef.current) return;
      if (document.visibilityState !== 'visible') {
        if (DEBUG) console.log('[櫃台-宴會] 輪詢 tick 跳過（頁面不可見）');
        return;
      }
      if (DEBUG) console.log('[櫃台-宴會] 輪詢 tick 執行，打 API', new Date().toLocaleTimeString('zh-TW'));
      fetchData(ymd, true).catch((e) => {
        console.warn('[櫃台-宴會] 輪詢失敗:', e?.message || e);
      });
    };

    const start = () => {
      if (stopped) return;
      if (DEBUG) console.log('[櫃台-宴會] 輪詢 start()', { visibility: document.visibilityState });
      tick();
      if (timer) clearInterval(timer);
      timer = setInterval(tick, POLL_MS);
    };

    const stop = () => {
      stopped = true;
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
      if (DEBUG) console.log('[櫃台-宴會] 輪詢 stop()');
    };

    const onVis = () => {
      if (document.hidden) {
        stop();
      } else {
        stopped = false;
        start();
      }
    };

    const onPageShow = () => {
      if (document.visibilityState === 'visible') {
        stopped = false;
        start();
      }
    };

    const onFocus = () => {
      if (document.visibilityState === 'visible') {
        stopped = false;
        start();
      }
    };

    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('focus', onFocus);
    start();

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('focus', onFocus);
      if (DEBUG) console.log('[櫃台-宴會] 輪詢 effect cleanup');
    };
  }, [ymd, fetchData]);

  // 可見度/聚焦 + 自動換日（業務日 01:00）- 更頻繁檢查日期變化
  useEffect(() => {
    if (todayOnly) {
      // todayOnly 模式下，日期應始終貼齊「業務日」
      let alive = true;

      const syncToday = () => {
        if (!alive || !mountedRef.current) return;
        const today = bizDay();
        if (today !== ymd) {
          console.log('[BanquetInfoPlaceholder] ⚠️ 檢測到日期改變:', ymd, '->', today, '立即刷新資料');
          manualDateRef.current = false;
          // 日期改變時，先清空資料，然後更新日期（會觸發資料重新載入）
          setEvents([]);
          setVenues([]);
          vnHashRef.current = 0;
          initialLoadRef.current = true;
          setYmd(today);
        }
      };
      
      // 更頻繁的跨「業務日」檢查：每 10 秒檢查一次日期是否改變
      const dayTick = setInterval(syncToday, 10000); // 每 10 秒檢查一次
      
      // 立即檢查一次
      syncToday();
      
      // 當頁面重新可見時，立即檢查日期
      const onVisible = () => {
        if (document.visibilityState === 'visible' && mountedRef.current) {
          syncToday();
        }
      };
      document.addEventListener('visibilitychange', onVisible);
      
      // 當視窗重新聚焦時，也檢查日期
      const onFocus = () => {
        if (mountedRef.current) {
          syncToday();
        }
      };
      window.addEventListener('focus', onFocus);

      return () => {
        alive = false;
        clearInterval(dayTick);
        document.removeEventListener('visibilitychange', onVisible);
        window.removeEventListener('focus', onFocus);
      };
    } else {
      // 非 todayOnly 模式：也需要自動切換日期，但保留手動切換的選項
      let alive = true;

      const checkAndUpdateDay = () => {
        if (!alive || !mountedRef.current) return;
        const now = bizDay();
        // 只有在用戶沒有手動切換日期時，才自動切回今天
        if (now !== ymd && !manualDateRef.current) {
          console.log('[BanquetInfoPlaceholder] 非 todayOnly 模式檢測到日期改變:', ymd, '->', now);
          setEvents([]);
          setVenues([]);
          vnHashRef.current = 0;
          initialLoadRef.current = true;
          setYmd(now);
        }
      };

      const onFocus = () => {
        if (mountedRef.current) {
          checkAndUpdateDay();
        }
      };
      
      const onVisible = () => {
        if (document.visibilityState === 'visible' && mountedRef.current) {
          checkAndUpdateDay();
        }
      };

      window.addEventListener('focus', onFocus);
      document.addEventListener('visibilitychange', onVisible);

      // 更頻繁的跨「業務日」偵測：每 10 秒檢查一次
      const dayTick = setInterval(checkAndUpdateDay, 10000);

      return () => {
        alive = false;
        clearInterval(dayTick);
        window.removeEventListener('focus', onFocus);
        document.removeEventListener('visibilitychange', onVisible);
        try {
          abortRef.current?.abort();
        } catch (e) {
          /* ignore */
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ymd, todayOnly]);

  const eventsToday = useMemo(
    () =>
      (events || [])
        .slice()
        .sort((a, b) => sortKeyEvent(a).localeCompare(sortKeyEvent(b))),
    [events]
  );
  const venuesToday = useMemo(
    () =>
      (venues || [])
        .filter(
          (v) => String(v?.STATUS || '').toLowerCase() !== 'cancelled'
        )
        .slice()
        .sort((a, b) => sortKeyVenue(a).localeCompare(sortKeyVenue(b))),
    [venues]
  );

  const emptyEvents = !loading && eventsToday.length === 0;
  const emptyVenues = !loading && venuesToday.length === 0;

  const isToday = useMemo(
    () => yyyyMMdd(parseDate(ymd)) === yyyyMMdd(new Date()),
    [ymd]
  );

  return (
    <section
      className="banquet-admin"
      style={{ ...st.wrap, gridTemplateRows: todayOnly ? '1fr' : 'auto 1fr' }}
    >
      <style>{`
        @media (max-width: 768px) {
          .banquet-admin { height: auto !important; min-height: 0; }
          .banquet-two-panes { grid-template-rows: auto auto !important; }
          .banquet-date-row { flex-wrap: wrap !important; }
        }
      `}</style>
      {/* 時間選項按鈕（僅在非 todayOnly 時顯示） */}
      {!todayOnly && bnNarrow && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 4px' }}>
          <button type="button" onClick={() => changeDate(-1)} style={{ padding: '8px 12px', fontWeight: 800, border: '1px solid #c7d2fe', borderRadius: 8, background: '#fff' }}>‹</button>
          <div style={{ fontWeight: 800, fontSize: 16 }}>{dateLabel}</div>
          <button type="button" onClick={() => changeDate(1)} style={{ padding: '8px 12px', fontWeight: 800, border: '1px solid #c7d2fe', borderRadius: 8, background: '#fff' }}>›</button>
          {!isToday && (
            <button type="button" onClick={() => { manualDateRef.current = false; setYmd(bizDay()); }} style={{ padding: '8px 10px', fontWeight: 800, border: 'none', borderRadius: 8, background: '#7b4f2f', color: '#fff' }}>今日</button>
          )}
        </div>
      )}
      {!todayOnly && !bnNarrow && (
        <div
            style={{
              padding: '10px 14px',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
              borderBottom: '2px solid rgba(99, 102, 241, 0.1)',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
            }}
        >
          <button
            onClick={() => changeDate(-1)}
            style={{
              padding: '10px 16px',
              fontSize: 15,
              border: '2px solid #e0e7ff',
              borderRadius: 10,
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
              cursor: 'pointer',
              fontWeight: 700,
              color: '#4c51bf',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
              e.currentTarget.style.color = '#fff';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)';
              e.currentTarget.style.color = '#4c51bf';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            ← 前一天
          </button>
          <div
            style={{
              fontSize: 18,
              fontWeight: 800,
              minWidth: 140,
              textAlign: 'center',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            {dateLabel}
          </div>
          <button
            onClick={() => changeDate(1)}
            style={{
              padding: '10px 16px',
              fontSize: 15,
              border: '2px solid #e0e7ff',
              borderRadius: 10,
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
              cursor: 'pointer',
              fontWeight: 700,
              color: '#4c51bf',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
              e.currentTarget.style.color = '#fff';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)';
              e.currentTarget.style.color = '#4c51bf';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            後一天 →
          </button>
          {!isToday && (
            <button
              onClick={() => {
                manualDateRef.current = false;
                setYmd(bizDay());
              }}
            style={{
              padding: '10px 16px',
              fontSize: 15,
              border: '2px solid #f5576c',
              borderRadius: 10,
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: '#fff',
              cursor: 'pointer',
              marginLeft: 'auto',
              fontWeight: 700,
              boxShadow: '0 4px 12px rgba(245, 87, 108, 0.3)',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(245, 87, 108, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(245, 87, 108, 0.3)';
            }}
            >
              回到今天
            </button>
          )}
        </div>
      )}
      {bnNarrow && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, padding: '0 4px 8px' }}>
          <button type="button" onClick={() => setPaneTab('events')} style={{ padding: 10, fontWeight: 800, borderRadius: 8, border: '1px solid #c7d2fe', background: paneTab === 'events' ? '#4c51bf' : '#fff', color: paneTab === 'events' ? '#fff' : '#4c51bf' }}>活動</button>
          <button type="button" onClick={() => setPaneTab('venues')} style={{ padding: 10, fontWeight: 800, borderRadius: 8, border: '1px solid #c7d2fe', background: paneTab === 'venues' ? '#4c51bf' : '#fff', color: paneTab === 'venues' ? '#fff' : '#4c51bf' }}>包場</button>
        </div>
      )}
      <div className="banquet-two-panes" style={{ ...st.twoPanes, ...(bnNarrow ? { gridTemplateRows: '1fr' } : {}) }}>
        {/* 活動 */}
        <div style={{ ...st.pane, ...(bnNarrow && paneTab !== 'events' ? { display: 'none' } : {}) }}>
          <div style={st.subhead}>
            🎉 {dateLabel.includes('今天') ? '今日活動' : '活動'}
          </div>
          <div style={st.scroller}>
            {loading && eventsToday.length === 0 ? (
              <div style={st.loading}>讀取中…</div>
            ) : emptyEvents ? (
              <div style={st.empty}>
                {isToday ? '今天沒有活動' : '該日沒有活動'}
              </div>
            ) : (
              <div style={st.list}>
                {eventsToday.map((ev, i) => {
                  const checkedIn = !!(ev.checked_in === true || ev.checked_in === 1 || ev.CHECKED_IN);
                  return (
                  <article
                    key={ev.id ?? i}
                    style={{
                      ...st.card,
                      ...(checkedIn ? st.cardCheckedIn : null),
                    }}
                  >
                    {checkedIn && (
                      <div style={st.checkedInBanner}>已進場無需再通知包場人員</div>
                    )}
                    <div style={st.time}>{fmtEventTime(ev) || '--:--'}</div>

                    <div style={st.rowLine}>
                      <b>{ev.title || '-'}</b>
                    </div>
                    <div style={st.rowLine}>
                      場地：<b>{ev.venue || '-'}</b>
                    </div>
                    <div style={st.rowLine}>
                      負責人：<b>{ev.owner || '-'}</b>
                    </div>

                    {ev.internal_brief && (
                      <div style={st.rowLine}>
                        <b>簡述活動內容：</b>
                        <span style={{ whiteSpace: 'pre-wrap' }}>{ev.internal_brief}</span>
                      </div>
                    )}
                    {ev.equipment_need && (
                      <div style={st.rowLine}>
                        <b>需求設備：</b>
                        <span style={{ whiteSpace: 'pre-wrap' }}>{ev.equipment_need}</span>
                      </div>
                    )}
                    {ev.beverage_need && (
                      <div style={st.alertGreen}>
                        <b>需要飲品：</b>
                        <span>{ev.beverage_need}</span>
                      </div>
                    )}
                    {ev.handover_notes && (
                      <div style={st.alertPurple}>
                        <b>交接事項：</b>
                        <span>{ev.handover_notes}</span>
                      </div>
                    )}
                  </article>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* 包場（加高） */}
        <div style={{ ...st.pane, ...(bnNarrow && paneTab !== 'venues' ? { display: 'none' } : {}) }}>
          <div style={st.subhead}>
            🏷️ {dateLabel.includes('今天') ? '今日包場' : '包場'}
          </div>
          <div style={st.scroller}>
            {loading && venuesToday.length === 0 ? (
              <div style={st.loading}>讀取中…</div>
            ) : emptyVenues ? (
              <div style={st.empty}>
                {isToday ? '今天沒有包場' : '該日沒有包場'}
              </div>
            ) : (
              <div style={st.list}>
                {venuesToday.map((v, i) => {
                  const flagged = isFlaggedVenue(v);
                  const checkedIn = !!(v.CHECKED_IN === 1 || v.CHECKED_IN === true || v.checked_in);
                  const activityName = v.activityName || v.ACTIVITY_NAME || '';
                  const activityTypeContent = v.activityTypeContent || v.ACTIVITY_TYPE_CONTENT || '';
                  const showActivity = !!(activityName || v.isActivityVenue);
                  const activityBrief = v.activityInternalBrief || v.internal_brief || '';
                  const activityEquip = v.activityEquipmentNeed || v.equipment_need || '';
                  return (
                    <article
                      key={v.ORDER_ID ?? i}
                      style={{
                        ...st.card,
                        ...(flagged ? st.cardPink : null),
                        ...(checkedIn ? st.cardCheckedIn : null),
                      }}
                    >
                      {checkedIn && (
                        <div style={st.checkedInBanner}>已進場無需再通知包場人員</div>
                      )}
                      <div style={st.time}>
                        {fmtVenueTime(v) || '--:--'}
                      </div>
                      {!flagged && !checkedIn && (
                        <div style={st.footnoteStrong}>
                          ＊包場開始前一小時關閉場地＊
                        </div>
                      )}

                      <div style={st.rowLine}>
                        <b>{v.VENUE || '-'}</b>
                      </div>

                      <div style={st.rowLineWrap}>
                        <b>{v.CUSTNAME || '-'}</b>
                        {v.PHONE && (
                          <span style={st.inlineSpace}>{v.PHONE}</span>
                        )}
                      </div>

                      {activityTypeContent && (
                        <div style={st.rowLine}>
                          活動類型與內容：<b>{activityTypeContent}</b>
                        </div>
                      )}

                      {showActivity && (
                        <div style={st.rowLine}>
                          活動：<b>{activityName || '-'}</b>
                        </div>
                      )}
                      {showActivity && activityBrief && (
                        <div style={st.rowLine}>
                          {activityBrief}
                        </div>
                      )}
                      {showActivity && activityEquip && (
                        <div style={st.rowLine}>
                          <b>設備：</b>{activityEquip}
                        </div>
                      )}

                      {v.COUNTER_NOTE && (
                        <div style={st.alertGreen}>
                          <b>櫃台備註：</b>
                          <span>{v.COUNTER_NOTE}</span>
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ====== 樣式 ====== */
const BASE = 15;
const st = {
  wrap: {
    height: '100%',
    display: 'grid',
    gridTemplateRows: '1fr', // 日期按鈕在條件渲染中，所以這裡統一用 1fr
    minHeight: 0,
    fontFamily: 'system-ui, "PingFang TC", "Microsoft JhengHei", sans-serif',
    color: '#111',
  },
  twoPanes: {
    minHeight: 0,
    display: 'grid',
    gridTemplateRows: '1fr 1.45fr',
    gap: 6,
  },
  pane: {
    minHeight: 0,
    display: 'grid',
    gridTemplateRows: 'auto 1fr',
    background: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)',
    border: '2px solid #e0e7ff',
    borderRadius: 12,
    boxShadow: '0 2px 6px rgba(99, 102, 241, 0.08)',
  },
  subhead: {
    padding: '10px 12px',
    fontSize: BASE + 1,
    fontWeight: 900,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#fff',
    borderBottom: '2px solid rgba(99, 102, 241, 0.2)',
    borderRadius: '12px 12px 0 0',
  },
  scroller: { minHeight: 0, overflowY: 'auto', padding: 8, paddingRight: 4 },

  loading: { padding: 6, fontSize: BASE - 1, color: '#666' },
  empty: { padding: 6, fontSize: BASE - 1, color: '#777' },

  list: { display: 'grid', gap: 6 },
  card: {
    borderRadius: 12,
    border: '2px solid rgba(99, 102, 241, 0.15)',
    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
    padding: 12,
    display: 'grid',
    gap: 6,
    boxShadow: '0 2px 6px rgba(99, 102, 241, 0.1)',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
  },
  cardPink: {
    border: '2px solid #f472b6',
    boxShadow: '0 4px 12px rgba(244, 114, 182, 0.25)',
    background: 'linear-gradient(135deg, #fdf2f8 0%, #fce7f3 100%)',
  },
  cardCheckedIn: {
    border: '3px solid #16a34a',
    background: '#dcfce7',
    boxShadow: '0 4px 12px rgba(22, 163, 74, 0.2)',
  },
  checkedInBanner: {
    fontSize: 14,
    fontWeight: 900,
    color: '#166534',
    marginBottom: 2,
  },

  time: { 
    fontSize: 24, 
    fontWeight: 900, 
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    lineHeight: 1 
  },

  rowLine: { fontSize: BASE + 1, lineHeight: 1.2, wordBreak: 'break-word' },

  rowLineWrap: {
    fontSize: BASE + 1,
    lineHeight: 1.35,
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
    display: 'inline',
  },
  inlineSpace: { marginLeft: 8 },

  alertGreen: {
    fontSize: BASE + 1,
    lineHeight: 1.4,
    color: '#065f46',
    fontWeight: 800,
    whiteSpace: 'pre-wrap',
    padding: '8px 12px',
    background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
    borderRadius: 8,
    border: '1px solid #10b981',
  },
  alertPurple: {
    fontSize: BASE + 1,
    lineHeight: 1.4,
    color: '#5b21b6',
    fontWeight: 800,
    whiteSpace: 'pre-wrap',
    padding: '8px 12px',
    background: 'linear-gradient(135deg, #e9d5ff 0%, #ddd6fe 100%)',
    borderRadius: 8,
    border: '1px solid #8b5cf6',
  },

  footnoteStrong: {
    fontSize: BASE,
    color: '#b45309',
    fontWeight: 900,
    marginTop: 2,
    marginBottom: 4,
    letterSpacing: '.04em',
  },
};
